import React, { createContext, useContext, useEffect, useState } from 'react';
import { AuthState, User } from '../types/userTypes';
import UserAuthService from '../services/userAuthService';
import { getCleanErrorMessage } from '../utils/errorUtils';

interface AuthContextType extends AuthState {
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string, displayName?: string) => Promise<void>;
    logout: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    updatePreferences: (preferences: Partial<User['preferences']>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [authState, setAuthState] = useState<AuthState>({
        user: null,
        isLoading: true,
        error: null,
        isAuthenticated: false
    });

    useEffect(() => {
        console.log('🔐 AuthContext: Initializing authentication...');
        try {
            const authService = UserAuthService.getInstance();
            console.log('🔐 AuthContext: UserAuthService instance created:', authService);
            const unsubscribe = authService.onAuthStateChanged((user) => {
                console.log('🔐 AuthContext: Auth state changed:', user ? 'User logged in' : 'No user');
                setAuthState({
                    user: user,
                    isAuthenticated: !!user,
                    isLoading: false,
                    error: null,
                });
            });

            return unsubscribe;
        } catch (error) {
            console.error('🔐 AuthContext: Error initializing authentication:', error);
            setAuthState(prev => ({ ...prev, isLoading: false, error: getCleanErrorMessage(error) }));
        }
    }, []);

    const login = async (email: string, password: string) => {
        console.log('🔐 AuthContext: Login attempt for:', email);
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const authService = UserAuthService.getInstance();
            console.log('🔐 AuthContext: Calling authService.login...');
            await authService.login(email, password);
            console.log('🔐 AuthContext: Login successful');
        } catch (error: any) {
            console.error('🔐 AuthContext: Login failed:', error);
            setAuthState(prev => ({ ...prev, error: getCleanErrorMessage(error), isLoading: false }));
            throw error;
        }
    };

    const register = async (email: string, password: string, displayName?: string) => {
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const authService = UserAuthService.getInstance();
            await authService.register(email, password, displayName);
        } catch (error: any) {
            setAuthState(prev => ({ ...prev, error: getCleanErrorMessage(error), isLoading: false }));
            throw error;
        }
    };

    const logout = async () => {
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const authService = UserAuthService.getInstance();
            await authService.logout();
        } catch (error: any) {
            setAuthState(prev => ({ ...prev, error: getCleanErrorMessage(error), isLoading: false }));
            throw error;
        }
    };

    const resetPassword = async (email: string) => {
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const authService = UserAuthService.getInstance();
            await authService.resetPassword(email);
        } catch (error: any) {
            setAuthState(prev => ({ ...prev, error: getCleanErrorMessage(error), isLoading: false }));
            throw error;
        }
    };

    const updatePreferences = async (preferences: Partial<User['preferences']>) => {
        try {
            const authService = UserAuthService.getInstance();
            await authService.updatePreferences(preferences);
        } catch (error: any) {
            setAuthState(prev => ({ ...prev, error: getCleanErrorMessage(error) }));
            throw error;
        }
    };

    const value: AuthContextType = {
        ...authState,
        login,
        register,
        logout,
        resetPassword,
        updatePreferences
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
